import { Calendar, MapPin, Loader2 } from "lucide-react";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";
import type { Trip, TripDate, Customer, Participant } from "./types";

interface ReviewStepProps {
  selectedTrip: Trip | null;
  selectedDate: TripDate | null;
  requestedDate: string;
  selectedCustomer: Customer | null;
  adultsCount: number;
  childrenCount: number;
  participants: Participant[];
  pricePerPerson: number;
  totalPrice: number;
  depositAmount: number;
  customerNotes: string;
  internalNotes: string;
  handleSubmit: () => void;
  createOrderPending: boolean;
  createOrderError: boolean;
  onBack: () => void;
}

function TripInfoSection({
  trip,
  date,
  requestedDate,
}: {
  trip: Trip | null;
  date: TripDate | null;
  requestedDate: string;
}) {
  const isExperience = trip?.tripKind === "experience";
  const dateText = date
    ? formatDate(date.startDate)
    : requestedDate
      ? `${formatDate(requestedDate)} (any-day)`
      : "—";
  const durationText = isExperience
    ? `${trip?.durationHours || 0} hours`
    : `${trip?.durationDays || 0} days`;
  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex items-start gap-4">
        {trip?.featuredImage && (
          <img
            src={trip.featuredImage}
            alt={trip.title}
            className="h-20 w-28 rounded-lg object-cover"
          />
        )}
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-white">{trip?.title}</h3>
          <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {dateText}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {durationText}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomerInfoSection({ customer }: { customer: Customer | null }) {
  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <h4 className="mb-2 font-medium text-gray-900 dark:text-white">Customer</h4>
      <p className="text-gray-600 dark:text-gray-300">
        {customer?.firstName} {customer?.lastName}
      </p>
      <p className="text-sm text-gray-500">{customer?.email}</p>
      {customer?.phone && <p className="text-sm text-gray-500">{customer.phone}</p>}
    </div>
  );
}

function PricingSection({
  adultsCount,
  childrenCount,
  price,
  totalPrice,
  depositAmount,
}: {
  adultsCount: number;
  childrenCount: number;
  price: number;
  totalPrice: number;
  depositAmount: number;
}) {
  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <h4 className="mb-2 font-medium text-gray-900 dark:text-white">Price</h4>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600 dark:text-gray-300">
            {adultsCount}x adult @ {formatPrice(price)}
          </span>
          <span className="text-gray-900 dark:text-white">{formatPrice(price * adultsCount)}</span>
        </div>
        {childrenCount > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-300">
              {childrenCount}x child (50%) @ {formatPrice(price * 0.5)}
            </span>
            <span className="text-gray-900 dark:text-white">
              {formatPrice(price * 0.5 * childrenCount)}
            </span>
          </div>
        )}
        <div className="flex justify-between border-t pt-2 dark:border-gray-600">
          <span className="font-semibold text-gray-900 dark:text-white">Total</span>
          <span className="text-primary-600 font-bold">{formatPrice(totalPrice)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Deposit (50%)</span>
          <span className="text-gray-600 dark:text-gray-300">{formatPrice(depositAmount)}</span>
        </div>
      </div>
    </div>
  );
}

function NotesSection({
  customerNotes,
  internalNotes,
}: {
  customerNotes: string;
  internalNotes: string;
}) {
  if (!customerNotes && !internalNotes) return null;
  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <h4 className="mb-2 font-medium text-gray-900 dark:text-white">Notes</h4>
      {customerNotes && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          <strong>Customer:</strong> {customerNotes}
        </p>
      )}
      {internalNotes && (
        <p className="mt-1 text-sm text-gray-500">
          <strong>Internal:</strong> {internalNotes}
        </p>
      )}
    </div>
  );
}

export default function ReviewStep({
  selectedTrip,
  selectedDate,
  requestedDate,
  selectedCustomer,
  adultsCount,
  childrenCount,
  participants,
  pricePerPerson,
  totalPrice,
  depositAmount,
  customerNotes,
  internalNotes,
  handleSubmit,
  createOrderPending,
  createOrderError,
  onBack,
}: ReviewStepProps) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Order summary</h2>

      <TripInfoSection trip={selectedTrip} date={selectedDate} requestedDate={requestedDate} />

      <CustomerInfoSection customer={selectedCustomer} />

      {/* Participants */}
      <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h4 className="mb-2 font-medium text-gray-900 dark:text-white">
          Travelers ({adultsCount} adults
          {childrenCount > 0 && `, ${childrenCount} children`})
        </h4>
        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
          {participants
            .filter((p) => p.name)
            .map((p, i) => (
              <li key={i}>{p.name}</li>
            ))}
        </ul>
      </div>

      <PricingSection
        adultsCount={adultsCount}
        childrenCount={childrenCount}
        price={pricePerPerson}
        totalPrice={totalPrice}
        depositAmount={depositAmount}
      />

      <NotesSection customerNotes={customerNotes} internalNotes={internalNotes} />

      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="btn-ghost">
          Back
        </button>
        <button onClick={handleSubmit} disabled={createOrderPending} className="btn-primary">
          {createOrderPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            "Create order"
          )}
        </button>
      </div>

      {createOrderError && (
        <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          Failed to create order. Please try again.
        </div>
      )}
    </div>
  );
}
